import type { Image, Region, ServerType } from "@cloudserver/contracts";
import { RefreshCw } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useCatalogue,
  useProject,
  useRefreshCatalogue,
  type CatalogueKind,
} from "../api/queries.js";
import { ErrorMessage } from "../ui/feedback.js";
import { money, relativeTime, terabytes, withUnit } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { PageHeader, Tabs } from "../ui/layout.js";
import { Badge, Button, Card, Select, Spinner } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";

export const CATALOGUE_TABS = ["server-types", "regions", "images", "prices"] as const;
export type CatalogueTab = (typeof CATALOGUE_TABS)[number];

const TAB_LABEL: Record<CatalogueTab, string> = {
  "server-types": "catalogue.serverTypes",
  regions: "catalogue.regions",
  images: "catalogue.images",
  prices: "catalogue.prices",
};

const TAB_INTRO: Record<CatalogueTab, string> = {
  "server-types": "catalogue.serverTypesIntro",
  regions: "catalogue.regionsIntro",
  images: "catalogue.imagesIntro",
  prices: "catalogue.pricesIntro",
};

function useCatalogueData<K extends CatalogueKind>(projectId: string, kind: K) {
  const query = useCatalogue(projectId, kind);
  const fallback: ReactNode = query.error ? <ErrorMessage error={query.error} /> : null;
  return { data: query.data, fallback };
}

function Regions({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { data, fallback } = useCatalogueData(projectId, "regions");
  if (fallback) return fallback;
  return (
    <DataTable<Region>
      label={t("catalogue.regions")}
      rows={data}
      rowKey={(r) => r.code}
      initialSort={{ key: "code", dir: "asc" }}
      columns={[
        {
          key: "city",
          header: t("catalogue.city"),
          primary: true,
          sort: (r) => r.city,
          cell: (r) => (
            <span className="font-medium">
              {r.city}, {r.country}
            </span>
          ),
        },
        {
          key: "code",
          header: t("catalogue.region"),
          sort: (r) => r.code,
          cell: (r) => <span className="font-mono">{r.code}</span>,
        },
        {
          key: "zone",
          header: (
            <span className="inline-flex items-center gap-1">
              {t("catalogue.zone")}
              <FieldHint text={t("catalogue.zoneHint")} topic="catalogue" />
            </span>
          ),
          sort: (r) => r.zone,
          cell: (r) => <span className="text-muted">{r.zone}</span>,
        },
      ]}
    />
  );
}

function price(type: ServerType, region: string): number {
  const p = type.prices.find((x) => x.region === region);
  return p ? Number(p.monthly.net) : Number.POSITIVE_INFINITY;
}

function ServerTypes({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const { data, fallback } = useCatalogueData(projectId, "server-types");
  const regions = useCatalogue(projectId, "regions").data ?? [];
  const prices = useCatalogue(projectId, "prices").data;
  const [region, setRegion] = useState("");
  const [family, setFamily] = useState("");
  const [arch, setArch] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const selectedRegion = region || regions[0]?.code || "";
  const currency = prices?.currency ?? "EUR";
  const available = (s: ServerType) =>
    Boolean(
      s.prices.some((x) => x.region === selectedRegion) &&
      s.regions.find((x) => x.region === selectedRegion)?.available,
    );

  const rows = data?.filter(
    (s) =>
      (!family || s.family === family) &&
      (!arch || s.architecture === arch) &&
      (!onlyAvailable || available(s)),
  );
  if (fallback) return fallback;

  const priceCell = (s: ServerType, pick: (p: ServerType["prices"][number]) => ReactNode) => {
    const p = s.prices.find((x) => x.region === selectedRegion);
    return p && available(s) ? pick(p) : <span className="text-muted">—</span>;
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          label={t("catalogue.region")}
          value={selectedRegion}
          onChange={(e) => setRegion(e.target.value)}
        >
          {regions.map((r) => (
            <option key={r.code} value={r.code}>
              {r.city} ({r.code})
            </option>
          ))}
        </Select>
        <Select
          label={t("catalogue.family")}
          value={family}
          onChange={(e) => setFamily(e.target.value)}
        >
          <option value="">{t("catalogue.allFamilies")}</option>
          {(["cost_optimized", "regular_purpose", "general_purpose"] as const).map((f) => (
            <option key={f} value={f}>
              {t(`catalogue.families.${f}`)}
            </option>
          ))}
        </Select>
        <Select
          label={t("catalogue.architecture")}
          value={arch}
          onChange={(e) => setArch(e.target.value)}
        >
          <option value="">{t("catalogue.allArchitectures")}</option>
          <option value="x86">x86</option>
          <option value="arm">Arm64</option>
        </Select>
        <label className="flex min-h-9 items-center gap-2 px-1 text-sm">
          <input
            type="checkbox"
            checked={onlyAvailable}
            onChange={(e) => setOnlyAvailable(e.target.checked)}
            className="accent-accent"
          />
          {t("catalogue.onlyAvailable")}
        </label>
      </div>
      <DataTable<ServerType>
        label={t("catalogue.serverTypes")}
        rows={rows}
        rowKey={(s) => s.code}
        muted={(s) => !available(s)}
        initialSort={{ key: "monthly", dir: "asc" }}
        columns={[
          {
            key: "type",
            header: t("catalogue.type"),
            primary: true,
            sort: (s) => s.code,
            cell: (s) => {
              const deprecated =
                s.deprecation || s.regions.find((x) => x.region === selectedRegion)?.deprecation;
              return (
                <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="font-mono font-medium">{s.code}</span>
                  <span className="text-xs text-muted">
                    {s.architecture === "arm" ? "Arm64" : "x86"} ·{" "}
                    {t(`catalogue.cpuKind.${s.cpuKind}`)}
                  </span>
                  {deprecated && <Badge tone="warn">{t("catalogue.deprecated")}</Badge>}
                  {!available(s) && <Badge>{t("catalogue.unavailableShort")}</Badge>}
                </span>
              );
            },
          },
          {
            key: "cpu",
            header: t("catalogue.cpu"),
            align: "right",
            sort: (s) => s.cores,
            cell: (s) => s.cores,
          },
          {
            key: "ram",
            header: t("catalogue.memory"),
            align: "right",
            sort: (s) => s.memoryGb,
            cell: (s) => withUnit(s.memoryGb, "GB"),
          },
          {
            key: "disk",
            header: t("catalogue.disk"),
            align: "right",
            sort: (s) => s.diskGb,
            cell: (s) => withUnit(s.diskGb, "GB"),
          },
          {
            key: "traffic",
            header: (
              <span className="inline-flex items-center gap-1">
                {t("catalogue.traffic")}
                <FieldHint text={t("catalogue.trafficHint")} />
              </span>
            ),
            align: "right",
            hideOnPhone: true,
            cell: (s) => priceCell(s, (p) => terabytes(p.includedTrafficBytes, i18n.language)),
          },
          {
            key: "hourly",
            header: t("catalogue.hourly"),
            align: "right",
            hideOnPhone: true,
            sort: (s) => price(s, selectedRegion),
            cell: (s) => priceCell(s, (p) => money(p.hourly.net, currency, i18n.language, 4)),
          },
          {
            key: "monthly",
            header: t("catalogue.monthly"),
            align: "right",
            sort: (s) => price(s, selectedRegion),
            cell: (s) =>
              priceCell(s, (p) => (
                <span className="font-medium">{money(p.monthly.net, currency, i18n.language)}</span>
              )),
          },
        ]}
      />
      <p className="text-xs text-muted">{t("catalogue.netPrices", { currency })}</p>
    </div>
  );
}

function Images({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { data, fallback } = useCatalogueData(projectId, "images");
  if (fallback) return fallback;
  return (
    <DataTable<Image>
      label={t("catalogue.images")}
      rows={data}
      rowKey={(image) => image.providerRef}
      initialSort={{ key: "os", dir: "asc" }}
      columns={[
        {
          key: "os",
          header: t("catalogue.os"),
          primary: true,
          sort: (image) => image.description,
          cell: (image) => (
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{image.description}</span>
              {image.rapidDeploy && <Badge tone="ok">{t("catalogue.rapidDeploy")}</Badge>}
            </span>
          ),
        },
        { key: "version", header: t("catalogue.version"), cell: (image) => image.osVersion ?? "—" },
        {
          key: "kind",
          header: t("catalogue.kind"),
          sort: (image) => image.kind,
          cell: (image) => (
            <span className="text-muted">{t(`catalogue.imageKinds.${image.kind}`)}</span>
          ),
        },
        {
          key: "arch",
          header: t("catalogue.architecture"),
          sort: (image) => image.architecture,
          cell: (image) => (image.architecture === "arm" ? "Arm64" : "x86"),
        },
      ]}
    />
  );
}

function Prices({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const { data, fallback } = useCatalogueData(projectId, "prices");
  if (fallback) return fallback;
  if (!data) return <Spinner label={t("common.loading")} />;
  const fmt = (amount: string, digits = 2) => money(amount, data.currency, i18n.language, digits);
  const ipv4 = data.publicIpv4[0];
  const rows: { label: string; hint: string; value: string }[] = [
    {
      label: t("catalogue.ipv4"),
      hint: t("catalogue.ipv4Hint"),
      value: ipv4
        ? t("catalogue.hourlyMonthly", {
            hourly: fmt(ipv4.hourly.net, 4),
            monthly: fmt(ipv4.monthly.net),
          })
        : "—",
    },
    {
      label: t("catalogue.volume"),
      hint: t("catalogue.volumeHint"),
      value: t("catalogue.perMonth", { price: fmt(data.volumePerGbMonth.net, 4) }),
    },
    {
      label: t("catalogue.snapshot"),
      hint: t("catalogue.snapshotHint"),
      value: t("catalogue.perMonth", { price: fmt(data.snapshotPerGbMonth.net, 4) }),
    },
    {
      label: t("catalogue.backups"),
      hint: t("catalogue.backupsHint"),
      value: t("catalogue.backupsValue", { percent: Number(data.backupPercent) }),
    },
  ];
  return (
    <Card className="flex flex-col gap-3 text-sm">
      <dl className="divide-y divide-line">
        {rows.map((row) => (
          <div key={row.label} className="grid gap-1 py-3 sm:grid-cols-[16rem_1fr] sm:gap-4">
            <dt className="flex items-center gap-1.5 text-muted">
              {row.label}
              <FieldHint text={row.hint} />
            </dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-muted">{t("catalogue.netPrices", { currency: data.currency })}</p>
    </Card>
  );
}

export function CataloguePage({ projectId, tab }: { projectId: string; tab: CatalogueTab }) {
  const { t, i18n } = useTranslation();
  const project = useProject(projectId);
  const refresh = useRefreshCatalogue();
  const toast = useToast();
  if (project.isPending) return <Spinner label={t("common.loading")} />;
  if (project.error) return <ErrorMessage error={project.error} />;
  const views: Record<CatalogueTab, ReactNode> = {
    "server-types": <ServerTypes projectId={projectId} />,
    regions: <Regions projectId={projectId} />,
    images: <Images projectId={projectId} />,
    prices: <Prices projectId={projectId} />,
  };
  const synced = project.data.connection?.lastSyncAt;
  return (
    <>
      <PageHeader
        title={t("nav.catalogue")}
        intro={t("catalogue.intro")}
        help="catalogue"
        meta={
          <span className="text-xs text-muted">
            {synced
              ? t("projects.synced", { date: relativeTime(synced, i18n.language) })
              : t("projects.neverSynced")}
          </span>
        }
        actions={
          <Button
            variant="secondary"
            busy={refresh.isPending}
            icon={<RefreshCw className="size-4" aria-hidden />}
            onClick={() =>
              refresh.mutate(projectId, {
                onSuccess: () =>
                  toast({
                    tone: "ok",
                    title: t("projects.refreshed", { name: project.data.name }),
                  }),
                onError: () => toast({ tone: "danger", title: t("common.unexpected") }),
              })
            }
          >
            {t("projects.refresh")}
          </Button>
        }
      />
      <Tabs
        label={t("nav.catalogue")}
        active={tab}
        items={CATALOGUE_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL[key]),
          link: {
            to: "/projects/$projectId/catalogue",
            params: { projectId },
            search: { tab: key },
          },
        }))}
      />
      <p className="-mt-2 text-sm text-muted">{t(TAB_INTRO[tab])}</p>
      {views[tab]}
    </>
  );
}
